import React from 'react';
import { Cpu, Shirt, Home, Utensils, Wrench, Sparkles, ArrowUpRight } from 'lucide-react';

const Products = () => {
  const productCategories = [
    {
      icon: Cpu,
      title: 'Tecnologia',
      description: 'Dispositivos e componentes de última geração',
      color: 'from-blue-600 to-cyan-500',
      image: 'https://images.unsplash.com/photo-1613690399151-65ea69478674'
    },
    {
      icon: Shirt,
      title: 'Têxtil',
      description: 'Moda e tecidos premium internacionais',
      color: 'from-purple-600 to-pink-500',
      image: 'https://images.unsplash.com/photo-1761307234387-d9291985eaf9'
    },
    {
      icon: Home,
      title: 'Casa & Decor',
      description: 'Móveis e itens decorativos exclusivos',
      color: 'from-amber-600 to-orange-500',
      image: 'https://images.unsplash.com/photo-1773126379692-342ff516ea53'
    },
    {
      icon: Utensils,
      title: 'Alimentos',
      description: 'Produtos gourmet e bebidas selecionadas',
      color: 'from-green-600 to-emerald-500',
      image: 'https://images.pexels.com/photos/753331/pexels-photo-753331.jpeg'
    },
    {
      icon: Wrench,
      title: 'Industrial',
      description: 'Equipamentos e ferramentas profissionais',
      color: 'from-slate-600 to-gray-700',
      image: 'https://images.unsplash.com/photo-1773126377223-d72cb000382d'
    },
    {
      icon: Sparkles,
      title: 'Sob Medida',
      description: 'Importação personalizada para você',
      color: 'from-[#C9A961] to-[#b89850]',
      image: 'https://images.unsplash.com/photo-1613690399151-65ea69478674'
    }
  ];

  return (
    <section id="products" className="py-32 bg-white relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute top-0 left-0 w-full h-full opacity-5">
        <div className="absolute top-20 right-20 w-72 h-72 bg-[#C9A961] rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 left-20 w-96 h-96 bg-[#2D5F7F] rounded-full blur-3xl"></div>
      </div>

      <div className="container mx-auto px-6 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-20">
          <div className="inline-flex items-center gap-2 bg-[#C9A961]/10 text-[#b89850] text-sm font-semibold px-4 py-2 rounded-full mb-6">
            <Sparkles className="w-4 h-4" />
            Catálogo Global
          </div>
          <h2 className="text-5xl md:text-6xl font-extrabold text-gray-900 mb-6">
            Ampla Variedade de{' '}
            <span className="bg-gradient-to-r from-[#C9A961] to-[#b89850] bg-clip-text text-transparent">
              Produtos
            </span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Conectamos você aos melhores produtos de diferentes segmentos do mercado internacional
          </p>
        </div>

        {/* Products Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-20 max-w-7xl mx-auto">
          {productCategories.map((category, index) => {
            const Icon = category.icon;
            return (
              <div
                key={index}
                className="group relative bg-white rounded-3xl overflow-hidden shadow-lg hover:shadow-2xl transition-all border border-gray-100 hover:border-[#C9A961]/30"
              >
                {/* Image Background */}
                <div className="relative h-64 overflow-hidden">
                  <img
                    src={category.image}
                    alt={category.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-gray-900 via-gray-900/60 to-transparent"></div>
                  
                  {/* Floating Icon */}
                  <div className={`absolute top-6 left-6 w-14 h-14 bg-gradient-to-br ${category.color} rounded-2xl flex items-center justify-center shadow-xl group-hover:scale-110 transition-transform`}>
                    <Icon className="w-7 h-7 text-white" />
                  </div>

                  {/* Title on Image */}
                  <div className="absolute bottom-6 left-6 right-6">
                    <h3 className="text-2xl font-bold text-white mb-2">
                      {category.title}
                    </h3>
                    <p className="text-gray-200 text-sm">
                      {category.description}
                    </p>
                  </div>
                </div>

                {/* Hover Action */}
                <div className="p-6 bg-gradient-to-br from-gray-50 to-white">
                  <button className="w-full flex items-center justify-between text-[#2D5F7F] font-semibold group-hover:text-[#C9A961] transition-colors">
                    <span>Explorar categoria</span>
                    <ArrowUpRight className="w-5 h-5 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
                  </button>
                </div>

                {/* Decorative Border */}
                <div className={`absolute inset-0 border-2 border-transparent group-hover:border-[#C9A961] rounded-3xl transition-all pointer-events-none`}></div>
              </div>
            );
          })}
        </div>

        {/* Bottom CTA */}
        <div className="max-w-4xl mx-auto text-center bg-gradient-to-br from-gray-50 to-white rounded-3xl p-12 border-2 border-gray-100">
          <div className="inline-block mb-6">
            <div className="w-20 h-20 bg-gradient-to-br from-[#2D5F7F] to-[#C9A961] rounded-2xl flex items-center justify-center mx-auto">
              <Sparkles className="w-10 h-10 text-white" />
            </div>
          </div>
          <h3 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Não encontrou o que procura?
          </h3>
          <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
            Realizamos <span className="font-semibold text-gray-900">importação sob demanda</span>. Entre em contato e descubra como podemos trazer exatamente o que seu negócio precisa.
          </p>
          <button
            onClick={() => {
              const element = document.getElementById('contact');
              if (element) element.scrollIntoView({ behavior: 'smooth' });
            }}
            className="group bg-gradient-to-r from-[#2D5F7F] to-[#1e4159] hover:from-[#1e4159] hover:to-[#2D5F7F] text-white font-bold px-10 py-5 rounded-xl shadow-xl hover:shadow-2xl transition-all transform hover:scale-105"
          >
            <span className="flex items-center gap-2">
              Solicitar Produto Específico
              <ArrowUpRight className="w-5 h-5 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
            </span>
          </button>
        </div>
      </div>
    </section>
  );
};

export default Products;
